<template>
  <PageLayout
    :is-loading="isLoading"
    page-title="Riwayat Redeem"
    :show-banner="false"
    :show-sponsors="false"
    :show-back-button="true"
    :hide-sidebar="true"
    @back="goBack"
  >

    <!-- User Profile Header -->
    <div class="profile-header">
      <div class="profile-info">
        <div class="profile-avatar">
          <img 
            :src="getUserImage()" 
            :alt="detailUser.name" 
            class="avatar-image"
            @error="handleImageError"
          />
        </div>
        <div class="profile-details">
          <h3 class="profile-name">{{ detailUser.name }}</h3>
          <p class="profile-community">{{ detailUser.community?.community || 'Community' }}</p>
        </div>
      </div>
      
      <div class="points-info">
        <div class="points-icon">
          <i class="mdi mdi-star"></i>
        </div>
        <div class="points-details">
          <p class="points-label">Total Poin</p>
          <h3 class="points-value">{{ getCurrentPoints() }}</h3>
        </div>
      </div>
    </div>

    <!-- Filter and Search Section -->
    <div class="filter-section">
      <div class="filter-container">
        <div class="search-wrapper">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Cari produk..."
            class="search-input"
          />
          <i class="mdi mdi-magnify search-icon"></i>
        </div>
        
        <div class="filter-wrapper">
          <select v-model="statusFilter" class="status-filter">
            <option value="">Semua Status</option>
            <option value="valid">Valid</option>
            <option value="pending">Pending</option>
            <option value="invalid">Invalid</option>
          </select>
          <i class="mdi mdi-chevron-down filter-icon"></i>
        </div>
      </div>
    </div>

    <!-- Redeem History List -->
    <div class="history-section">
      <div v-if="filteredRedeems.length === 0 && !isLoading" class="empty-state">
        <div class="empty-icon">
          <i class="mdi mdi-package-variant-closed"></i>
        </div>
        <h3 class="empty-title">{{ searchQuery || statusFilter ? 'Tidak Ada Hasil' : 'Belum Ada Riwayat Redeem' }}</h3>
        <p class="empty-description">
          {{ searchQuery || statusFilter ? 'Coba ubah filter atau kata kunci pencarian' : 'Kamu belum pernah melakukan redeem produk' }}
        </p>
        <bert-button 
          v-if="!searchQuery && !statusFilter"
          variant="primary" 
          size="md" 
          @click="goToRedeem"
          class="empty-action-btn"
        >
          Mulai Redeem
        </bert-button>
      </div>

      <div v-else class="redeem-list">
        <div 
          v-for="(redeem, index) in filteredRedeems" 
          :key="redeem.id" 
          class="redeem-item"
          :class="{ 'last-item': index === filteredRedeems.length - 1 }"
        >
          <div class="redeem-icon">
            <i class="mdi mdi-gift"></i>
          </div>
          
          <div class="redeem-content">
            <div class="redeem-header">
              <h4 class="redeem-title">{{ redeem.produk }}</h4>
              <div class="redeem-status" :class="`status-${redeem.status}`">
                <i :class="getStatusIcon(redeem.status)"></i>
                <span class="status-text">{{ getStatusText(redeem.status) }}</span>
              </div>
            </div>
            
            <div class="redeem-details">
              <div class="detail-item">
                <i class="mdi mdi-star detail-icon"></i>
                <span class="detail-text">{{ redeem.point }} Poin</span>
              </div>
              
              <div v-if="redeem.created_at" class="detail-item">
                <i class="mdi mdi-calendar detail-icon"></i>
                <span class="detail-text">{{ formatDate(redeem.created_at) }}</span>
              </div>
              
              <div v-if="redeem.wa" class="detail-item">
                <i class="mdi mdi-phone detail-icon"></i>
                <span class="detail-text">{{ redeem.wa }}</span>
              </div>
            </div>
            
            <div v-if="redeem.alamat_lengkap" class="redeem-address">
              <i class="mdi mdi-map-marker address-icon"></i>
              <p class="address-text">{{ getFullAddress(redeem) }}</p>
            </div>
            
            <!-- Status Message -->
            <div v-if="getStatusMessage(redeem.status)" class="status-message" :class="`message-${redeem.status}`">
              <i :class="getStatusIcon(redeem.status)"></i>
              <span>{{ getStatusMessage(redeem.status) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div v-if="redeems.length > 0" class="stats-section">
      <div class="stats-container">
        <div class="stat-item">
          <div class="stat-number">{{ getStatusCount('valid') }}</div>
          <div class="stat-label">Berhasil</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <div class="stat-number">{{ getStatusCount('pending') }}</div>
          <div class="stat-label">Pending</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <div class="stat-number">{{ getStatusCount('invalid') }}</div>
          <div class="stat-label">Ditolak</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <div class="stat-number">{{ getTotalPoints() }}</div>
          <div class="stat-label">Total Poin</div>
        </div>
      </div>
    </div>

    <!-- Action Button -->
    <div class="action-section">
      <bert-button 
        variant="primary" 
        size="lg" 
        @click="goToRedeem"
        full-width
        class="redeem-action-btn"
      >
        <i class="mdi mdi-plus"></i>
        Redeem Produk Lagi
      </bert-button>
    </div>
  </PageLayout>
</template>

<script>
import AppPageLayout from '@/components/ui/PageLayout.vue';
import BertButton from '@/components/ui/Button.vue';

export default {
  name: "NewRedeem",
  components: {
    PageLayout: AppPageLayout,
    'bert-button': BertButton
  },
  data() {
    return {
      isLoading: false,
      detailUser: {},
      redeems: [],
      searchQuery: '',
      statusFilter: '',
      errors: []
    };
  },
  computed: {
    filteredRedeems() {
      let filtered = this.redeems;
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(redeem => 
          redeem.produk.toLowerCase().includes(query)
        );
      }
      
      // Filter by status
      if (this.statusFilter) {
        filtered = filtered.filter(redeem => 
          redeem.status === this.statusFilter
        );
      }
      
      return filtered;
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    goToRedeem() {
      this.$router.push('/user/redeem-produk');
    },
    
    loadRedeemHistory() {
      const baseURI = this.$settings.endPoint + "redeem-produk/select";
      this.isLoading = true;
      
      return this.$http
        .get(baseURI, { 
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          this.redeems = response.data.datas || [];
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },
    
    fetchUserData() {
      const userDatas = this.$getUserInfo();
      this.detailUser = userDatas.sub || {};
    },
    
    getUserImage() {
      if (this.detailUser.image && this.detailUser.image.trim() !== '') {
        return this.detailUser.image;
      }
      return require('@/assets/maternity.png');
    },
    
    handleImageError(event) {
      event.target.src = require('@/assets/maternity.png');
    },
    
    getCurrentPoints() {
      if (this.redeems.length > 0 && this.redeems[0].user?.point !== undefined) {
        return this.redeems[0].user.point;
      }
      return this.detailUser.point || 0;
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'valid':
          return 'mdi mdi-check-circle';
        case 'invalid':
          return 'mdi mdi-close-circle';
        case 'pending':
        default:
          return 'mdi mdi-clock-outline';
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case 'valid':
          return 'Berhasil';
        case 'invalid':
          return 'Ditolak';
        case 'pending':
        default:
          return 'Pending';
      }
    },
    
    getStatusMessage(status) {
      switch (status) {
        case 'valid':
          return 'Redeem berhasil diproses dan produk akan segera dikirim';
        case 'invalid':
          return 'Redeem ditolak, silakan hubungi admin untuk informasi lebih lanjut';
        case 'pending':
          return 'Redeem sedang diproses, mohon tunggu konfirmasi admin';
        default:
          return '';
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return date.toLocaleDateString('id-ID', options);
    },
    
    getFullAddress(redeem) {
      const addressParts = [
        redeem.alamat_lengkap,
        redeem.kelurahan,
        redeem.kecamatan,
        redeem.kota,
        redeem.provinsi,
        redeem.kode_pos
      ].filter(part => part && part.trim() !== '');
      
      return addressParts.join(', ');
    },
    
    getStatusCount(status) {
      return this.redeems.filter(redeem => redeem.status === status).length;
    },
    
    getTotalPoints() {
      return this.redeems.reduce((total, redeem) => {
        return total + (parseInt(redeem.point) || 0);
      }, 0);
    },
    
    handleError(error) {
      if (error.response) {
        if (error.response.status === 422) {
          this.handleErrors(error.response.data.errors);
        } else if (error.response.status === 500) {
          this.$router.push('/server-error');
        } else if (error.response.status === 401) {
          this.$router.push("/authorized-error");
        } else {
          this.$router.push('/page-not-found');
        }
      } else if (error.code === "ECONNABORTED") {
        this.error("Please check your internet connection");
      } else {
        this.error("An error occurred. Please try again.");
      }
    },
    
    handleErrors(errors) {
      this.errors = errors;
      if (errors) {
        Object.keys(errors).forEach(key => {
          if (errors[key] && errors[key].length > 0) {
            this.error(errors[key][0]);
          }
        });
      }
    },
    
    success(message) {
      this.$swal({
        title: 'Berhasil!',
        text: message,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#F2994A'
      });
    },
    
    error(message) {
      this.$swal({
        title: 'Error!',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#F2994A'
      });
    }
  },
  
  mounted() {
    this.fetchUserData();
    this.loadRedeemHistory();
  }
};
</script>

<style scoped>
/* ========================================= */
/* PROFILE HEADER SECTION                    */
/* ========================================= */
.profile-header {
  background: linear-gradient(135deg, #F2994A 0%, #F2C94C 100%);
  padding: 24px 32px;
  margin: 0 auto 32px auto;
  border-radius: 16px;
  max-width: 1200px;
  width: calc(100% - 64px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.profile-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.profile-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-details {
  display: flex;
  flex-direction: column;
}

.profile-name {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.profile-community {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0;
}

.points-info {
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: right;
}

.points-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.points-details {
  display: flex;
  flex-direction: column;
}

.points-label {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0 0 4px 0;
}

.points-value {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

/* ========================================= */
/* FILTER SECTION                            */
/* ========================================= */
.filter-section {
  padding: 0 32px;
  margin: 0 auto 24px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.filter-container {
  display: flex;
  gap: 16px;
  align-items: center;
}

.search-wrapper {
  flex: 1;
  position: relative;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #F2994A;
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.1);
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  font-size: 1.1rem;
}

.filter-wrapper {
  position: relative;
  min-width: 160px;
}

.status-filter {
  width: 100%;
  padding: 12px 44px 12px 16px;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
}

.status-filter:focus {
  outline: none;
  border-color: #F2994A;
  box-shadow: 0 0 0 3px rgba(242, 153, 74, 0.1);
}

.filter-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  font-size: 1.1rem;
  pointer-events: none;
}

/* ========================================= */
/* HISTORY SECTION                           */
/* ========================================= */
.history-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.redeem-list {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.redeem-item {
  display: flex;
  padding: 24px;
  border-bottom: 1px solid #F3F4F6;
  transition: all 0.3s ease;
}

.redeem-item:hover {
  background: #F9FAFB;
}

.redeem-item.last-item {
  border-bottom: none;
}

.redeem-icon {
  width: 48px;
  height: 48px;
  background: #F0F9FF;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 1.5rem;
  color: #0EA5E9;
  flex-shrink: 0;
}

.redeem-content {
  flex: 1;
}

.redeem-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.redeem-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0;
  flex: 1;
  margin-right: 16px;
}

.redeem-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.redeem-status.status-valid {
  background: #DCFCE7;
  color: #166534;
}

.redeem-status.status-invalid {
  background: #FEE2E2;
  color: #991B1B;
}

.redeem-status.status-pending {
  background: #FEF3C7;
  color: #92400E;
}

.redeem-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #6B7280;
}

.detail-icon {
  font-size: 1rem;
}

.redeem-address {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: #F9FAFB;
  border-radius: 8px;
}

.address-icon {
  font-size: 1rem;
  color: #6B7280;
  margin-top: 2px;
  flex-shrink: 0;
}

.address-text {
  font-size: 0.85rem;
  color: #6B7280;
  line-height: 1.4;
  margin: 0;
}

.status-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-message.message-valid {
  background: #DCFCE7;
  color: #166534;
}

.status-message.message-invalid {
  background: #FEE2E2;
  color: #991B1B;
}

.status-message.message-pending {
  background: #FEF3C7;
  color: #92400E;
}

/* ========================================= */
/* EMPTY STATE                               */
/* ========================================= */
.empty-state {
  background: white;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #F3F4F6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  font-size: 2.5rem;
  color: #9CA3AF;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1F2937;
  margin: 0 0 8px 0;
}

.empty-description {
  color: #6B7280;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.empty-action-btn {
  max-width: 200px;
}

/* ========================================= */
/* STATS SECTION                             */
/* ========================================= */
.stats-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.stats-container {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  font-size: 1.8rem;
  font-weight: 800;
  color: #F2994A;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.85rem;
  color: #6B7280;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: #E5E7EB;
  margin: 0 16px;
}

/* ========================================= */
/* ACTION SECTION                            */
/* ========================================= */
.action-section {
  padding: 0 32px;
  margin: 0 auto 32px auto;
  max-width: 1200px;
  width: calc(100% - 64px);
}

.redeem-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* ========================================= */
/* MOBILE RESPONSIVE STYLES                  */
/* ========================================= */
@media (max-width: 768px) {
  .profile-header,
  .filter-section,
  .history-section,
  .stats-section,
  .action-section {
    width: calc(100% - 40px);
    margin: 0 20px 24px 20px;
    padding: 16px;
  }
  
  .profile-header {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .profile-info {
    flex-direction: column;
    text-align: center;
  }
  
  .points-info {
    justify-content: center;
  }
  
  .filter-container {
    flex-direction: column;
    gap: 12px;
  }
  
  .filter-wrapper {
    width: 100%;
    min-width: auto;
  }
  
  .redeem-item {
    flex-direction: column;
    gap: 16px;
  }
  
  .redeem-icon {
    align-self: flex-start;
    margin-right: 0;
  }
  
  .redeem-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .redeem-title {
    margin-right: 0;
  }
  
  .redeem-details {
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-container {
    flex-direction: column;
    gap: 20px;
  }
  
  .stat-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }
  
  .empty-action-btn {
    max-width: 100%;
  }
}
</style>